<template>
  <form-section
    :title="$t('Automations_Title_Attributes')"
    :description="$t('Automations_Title_Attributes_Description')"
  >
    <draggable-table>
      <el-table
        :show-header="false"
        class="table"
        :data="data"
        stripe
        v-loading="addingInProgress"
      >
        <el-table-column>
          <template slot-scope="scope">
            {{ scope.row }}
          </template>
        </el-table-column>

        <el-table-column width="40" align="center">
          <template>
            <i class="el-icon-rank"></i>
          </template>
        </el-table-column>

        <el-table-column width="40" align="center">
          <template slot-scope="scope">
            <i class="el-icon-delete" @click="deleteAttr(scope.$index)"></i>
          </template>
        </el-table-column>
      </el-table>
    </draggable-table>

    <el-button
      class="m-top-20"
      icon="el-icon-circle-plus-outline"
      type="primary"
      plain
      @click="showSelect()"
    >
      {{ $t('Audiences_Segment_Button_AddAttribute') }}
    </el-button>

    <el-select
      v-show="selectVisible"
      v-model="newAttr"
      ref="select"
      @visible-change="selectVisibleChange"
      @change="addAttr"
      :loading="selectLoading"
      :placeholder="$t('Audiences_Segment_Text_SelectAttribute')"
      filterable
      remote
      :remote-method="fetchAttributesRemote"
    >
      <el-option
        v-if="attributesAvailable"
        value="all"
        key="all_attributes"
        :label="$t('AttributesList_AllAttributes')"
      />
      <el-option-group :label="$t('Audiences_Segment_Text_StandardAttributes')">
        <el-option
          v-for="(item, index) in availableStandardAttributes"
          :key="index"
          :label="item"
          :value="item"
        />
      </el-option-group>

      <el-option-group
        v-if="availableCustomAttributes.length > 0"
        :label="$t('Audiences_Segment_Text_CustomAttributes')"
      >
        <el-option
          v-for="(item, index) in availableCustomAttributes"
          :key="index"
          :label="item.name"
          :value="item.name"
        />
      </el-option-group>

      <el-option-group
        v-if="availableComputedAttributes.length > 0"
        :label="$t('Audiences_Segment_Text_ComputedAttributes')"
      >
        <el-option
          v-for="(item, index) in availableComputedAttributes"
          :key="index"
          :label="item.name"
          :value="item.name"
        />
      </el-option-group>
    </el-select>
  </form-section>
</template>

<script>
import axios from 'axios'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import DraggableTable from '@/common/components/table/DraggableTable'
import FormSection from '@/common/components/form/FormSection'

export default {
  name: 'AttributesListDraggable',

  components: { DraggableTable, FormSection },

  props: {
    profileAttributes: {
      type: Boolean,
      required: false,
      default: false
    },
    computedAttributes: {
      type: Boolean,
      required: false,
      default: false
    },
    deviceAttributes: {
      type: Boolean,
      required: false,
      default: false
    },
    campaignAttributes: {
      type: Boolean,
      required: false,
      default: false
    },
    value: {
      required: true
    }
  },

  data() {
    return {
      data: [],
      attributes: [],
      attributesPages: null,
      newAttr: null,
      selectVisible: false,
      selectLoading: false,
      addingInProgress: false,
      filterableAttributes: [
        'user_id',
        'customer_id',
        'email',
        'mobile_number',
        'whatsapp_number',
        'language',
        'timezone',
        'email_addressable',
        'email_subscription',
        'sms_addressable',
        'sms_subscription'
      ],
      search: ''
    }
  },

  created() {
    this.data = this.value || []

    this.fetchAttributes()
  },

  computed: {
    attributesAvailable() {
      return (
        this.availableStandardAttributes.length +
          this.availableFetchedAttributes.length !==
        0
      )
    },

    availableStandardAttributes() {
      return this.standardAttributes.filter(opt => !this.data.includes(opt))
    },

    availableFetchedAttributes() {
      return this.attributes.filter(opt => !this.data.includes(opt.name))
    },

    availableCustomAttributes() {
      return this.availableFetchedAttributes.filter(opt => opt.aggregate === 0)
    },

    availableComputedAttributes() {
      return this.availableFetchedAttributes.filter(opt => opt.aggregate === 1)
    },

    standardAttributes() {
      let filterableAttributes = this.filterableAttributes
      if (!this.$store.state.app.features.customerIdEnabled) {
        filterableAttributes = filterableAttributes.filter(
          item => item !== 'customer_id'
        )
      }
      if (this.search === '') {
        return filterableAttributes
      } else {
        return filterableAttributes.filter(e => e.includes(this.search))
      }
    },

    attributesEndpoint() {
      let params = { sort: 'title' }
      if (this.profileAttributes) {
        params.attribute_profile = 1
      }
      if (this.deviceAttributes) {
        params.attribute_client = 1
      }
      if (this.campaignAttributes) {
        params.attribute_campaign = 1
      }
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: params
      })
    },

    computedAttributesEndpoint() {
      let params = { sort: 'title', aggregate: 1 }
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: params
      })
    }
  },

  methods: {
    showSelect() {
      this.search = ''
      this.fetchAttributesRemote('')
      this.selectVisible = true
      setTimeout(() => {
        this.$refs['select'].focus()
      }, 0)
    },

    selectVisibleChange(visible) {
      if (!visible) {
        this.selectVisible = false
      }
    },

    addAttr(attr) {
      this.addingInProgress = true
      if (attr === 'all') {
        let currentPage = 1
        const requests = []

        const params = {}

        if (this.profileAttributes) {
          params.attribute_profile = 1
        }

        if (this.deviceAttributes) {
          params.attribute_client = 1
        }

        if (this.campaignAttributes) {
          params.attribute_campaign = 1
        }

        while (currentPage <= this.attributesPages) {
          requests.push(
            RestApi({
              ...this.attributesEndpoint,
              params: {
                ...params,
                page: currentPage
              }
            })
          )
          currentPage++
        }

        if (this.computedAttributes) {
          requests.push(
            RestApi({
              ...this.computedAttributesEndpoint
            })
          )
        }

        axios
          .all(requests)
          .then(
            axios.spread((...responses) => {
              const attributes = [...this.standardAttributes]

              responses.forEach(response =>
                attributes.push(...response.data.map(item => item.name))
              )
              this.data.push(
                ...attributes.filter(attr => !this.data.includes(attr))
              )
              this.addingInProgress = false
            })
          )
          .catch(error => {
            this.addingInProgress = false
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      } else {
        this.data.push(attr)
        this.addingInProgress = false
      }

      this.newAttr = null
    },

    deleteAttr(index) {
      this.data.splice(index, 1)
    },

    fetchAttributes() {
      this.selectLoading = true
      RestApi(this.attributesEndpoint)
        .then(({ data }) => {
          this.attributes = data
          this.selectLoading = false
        })
        .catch(error => {
          this.selectLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchAttributesRemote(query) {
      this.search = query

      let params = { sort: 'title' }

      if (this.profileAttributes) {
        params.attribute_profile = 1
      }

      if (this.deviceAttributes) {
        params.attribute_client = 1
      }

      if (this.campaignAttributes) {
        params.attribute_campaign = 1
      }

      if (query !== null && query !== '') {
        params.title = query
      }

      RestApi({
        ...this.attributesEndpoint,
        params: params
      })
        .then(({ data, headers }) => {
          this.attributes = data
          this.attributesPages = parseInt(
            headers['x-pagination-page-count'] || '1'
          )
          if (this.computedAttributes) {
            this.fetchComputedAttributes(query)
          }
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    fetchComputedAttributes(query) {
      let params = { sort: 'title', aggregate: 1 }

      if (query !== null && query !== '') {
        params.title = query
      }

      RestApi({
        ...this.computedAttributesEndpoint,
        params: params
      })
        .then(({ data }) => {
          data.forEach(item => {
            this.attributes.push(item)
          })
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  },

  watch: {
    data: {
      handler: function(value) {
        this.$emit('input', value)
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-table {
  border-radius: 4px;
  border: 1px solid #dbdee7;
}

/deep/ .el-select input {
  height: 36px;
}
</style>



// WEBPACK FOOTER //
// src/modules/automations/components/AttributesListDraggable.vue